// KitchenTicket.jsx
// Browser print component — Tier 1 print solution
// Renders a kitchen-optimised ticket and triggers window.print()
// Styled for 80mm thermal paper width

import React from 'react';

const PRINT_STYLES = `
  @media print {
    @page {
      size: 80mm auto;
      margin: 4mm 3mm;
    }
    body * { visibility: hidden !important; }
    .kitchen-ticket,
    .kitchen-ticket * { visibility: visible !important; }
    .kitchen-ticket {
      position: fixed !important;
      top: 0 !important;
      left: 0 !important;
      width: 74mm !important;
    }
    .no-print { display: none !important; }
  }
`;

export function printKitchenTicket(ticket) {
  // Inject print styles if not already present
  if (!document.getElementById('kitchen-print-styles')) {
    const style = document.createElement('style');
    style.id = 'kitchen-print-styles';
    style.innerHTML = PRINT_STYLES;
    document.head.appendChild(style);
  }

  // Create a temporary ticket container
  const existing = document.getElementById('kitchen-ticket-container');
  if (existing) existing.remove();

  const container = document.createElement('div');
  container.id = 'kitchen-ticket-container';
  container.className = 'kitchen-ticket';
  container.style.cssText = `
    position: fixed; top: -9999px; left: -9999px;
    width: 74mm; background: white; z-index: 99999;
    font-family: 'Courier New', Courier, monospace;
    font-size: 12px; line-height: 1.4; color: #000;
    padding: 4mm 3mm;
  `;

  container.innerHTML = buildTicketHTML(ticket);
  document.body.appendChild(container);

  // Small delay to ensure DOM is painted before print dialog
  setTimeout(() => {
    window.print();
    setTimeout(() => container.remove(), 1000);
  }, 100);
}

function buildTicketHTML(ticket) {
  const {
    type, ticketNumber, customerName, customerPhone, timestamp,
    items, pickupTime, fulfillmentMethod, deliveryAddress,
    partySize, reservationDate, reservationTime, specialRequests,
  } = ticket;

  const divider = `<div style="border-top: 2px solid #000; margin: 6px 0;"></div>`;
  const thinDiv  = `<div style="border-top: 1px dashed #000; margin: 4px 0;"></div>`;

  const row = (label, value) => value
    ? `<div style="display:flex; justify-content:space-between; margin: 2px 0;">
        <span style="font-weight:bold;">${label}</span>
        <span style="text-align:right; max-width:55mm; word-break:break-word;">${value}</span>
       </div>`
    : '';

  let html = '';

  // ── HEADER ──────────────────────────────────────────────────────────────
  html += `<div style="text-align:center; font-size:16px; font-weight:900; letter-spacing:1px; margin-bottom:4px;">`;
  html += type === 'order' ? '*** NEW ORDER ***' : '*** NEW RESERVATION ***';
  html += `</div>`;
  html += `<div style="text-align:center; font-size:11px; color:#333; margin-bottom:2px;">${ticketNumber}</div>`;
  html += `<div style="text-align:center; font-size:10px; color:#555;">${timestamp}</div>`;
  html += divider;

  // ── CUSTOMER ────────────────────────────────────────────────────────────
  html += row('CUSTOMER', customerName);
  if (customerPhone) html += row('PHONE', customerPhone);
  html += divider;

  if (type === 'order') {
    // ── ITEMS ──────────────────────────────────────────────────────────────
    html += `<div style="font-weight:bold; margin-bottom:4px;">ITEMS:</div>`;
    (items || []).forEach(item => {
      const qty  = item.qty || item.quantity || 1;
      const name = item.dish_name || item.name || '';
      html += `<div style="margin: 3px 0;">`;
      html += `<span style="font-weight:bold; font-size:13px;">${qty}x</span> ${name}`;
      html += `</div>`;
      if (item.notes) {
        html += `<div style="padding-left:12px; color:#333; font-size:11px;">↳ ${item.notes}</div>`;
      }
    });
    html += divider;

    // ── FULFILLMENT ────────────────────────────────────────────────────────
    if (fulfillmentMethod === 'delivery') {
      html += `<div style="font-weight:bold; font-size:13px; margin-bottom:3px;">🚗 DELIVERY</div>`;
      if (deliveryAddress?.street) {
        html += `<div style="font-size:11px;">${deliveryAddress.street}</div>`;
        if (deliveryAddress.apartment) html += `<div style="font-size:11px;">${deliveryAddress.apartment}</div>`;
        html += `<div style="font-size:11px;">${deliveryAddress.city}, ${deliveryAddress.state} ${deliveryAddress.zipCode}</div>`;
        if (deliveryAddress.instructions) {
          html += thinDiv;
          html += `<div style="font-size:11px; font-style:italic;">Note: ${deliveryAddress.instructions}</div>`;
        }
      }
    } else {
      html += `<div style="font-weight:bold; font-size:13px;">🏃 PICKUP</div>`;
    }
    if (pickupTime) {
      html += `<div style="font-size:14px; font-weight:900; margin-top:4px;">TIME: ${pickupTime}</div>`;
    }

  } else {
    // ── RESERVATION DETAILS ────────────────────────────────────────────────
    html += `<div style="font-size:14px; font-weight:900; margin: 3px 0;">${reservationDate}</div>`;
    html += `<div style="font-size:14px; font-weight:900; margin: 3px 0;">${reservationTime}</div>`;
    html += `<div style="font-size:13px; margin: 3px 0;">Party of <strong>${partySize}</strong></div>`;
    if (specialRequests && specialRequests !== 'none') {
      html += thinDiv;
      html += `<div style="font-size:11px; font-style:italic;">Notes: ${specialRequests}</div>`;
    }
  }

  html += divider;
  html += `<div style="text-align:center; font-size:10px; color:#888; margin-top:4px;">Printed via TableChat</div>`;
  html += `<div style="height:20mm;"></div>`; // paper feed

  return html;
}

// ── REACT COMPONENT (for portal preview) ──────────────────────────────────
export default function KitchenTicket({ ticket, onClose }) {
  const {
    type, ticketNumber, customerName, customerPhone, timestamp,
    items, pickupTime, fulfillmentMethod, deliveryAddress,
    partySize, reservationDate, reservationTime, specialRequests,
  } = ticket;

  const handlePrint = () => printKitchenTicket(ticket);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'white', borderRadius: 12, width: 360, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        {/* Modal header */}
        <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>Kitchen Ticket Preview</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--muted)' }}>✕</button>
        </div>

        {/* Ticket preview — 80mm simulation */}
        <div style={{ padding: '1rem', background: '#f9f9f9' }}>
          <div style={{
            background: 'white', border: '1px solid #ddd', borderRadius: 4,
            padding: '12px 10px', fontFamily: "'Courier New', monospace",
            fontSize: 12, lineHeight: 1.5, maxWidth: 300, margin: '0 auto',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}>
            {/* Header */}
            <div style={{ textAlign: 'center', fontWeight: 900, fontSize: 15, letterSpacing: 1, marginBottom: 4 }}>
              {type === 'order' ? '*** NEW ORDER ***' : '*** NEW RESERVATION ***'}
            </div>
            <div style={{ textAlign: 'center', fontSize: 11, color: '#555' }}>{ticketNumber}</div>
            <div style={{ textAlign: 'center', fontSize: 10, color: '#888', marginBottom: 6 }}>{timestamp}</div>
            <hr style={{ border: 'none', borderTop: '2px solid #000', margin: '6px 0' }} />

            {/* Customer */}
            <div style={{ display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
              <strong>CUSTOMER</strong><span>{customerName}</span>
            </div>
            {customerPhone && (
              <div style={{ display: 'flex', justifyContent: 'space-between', margin: '2px 0' }}>
                <strong>PHONE</strong><span>{customerPhone}</span>
              </div>
            )}
            <hr style={{ border: 'none', borderTop: '2px solid #000', margin: '6px 0' }} />

            {type === 'order' ? (
              <>
                <div style={{ fontWeight: 'bold', marginBottom: 4 }}>ITEMS:</div>
                {(items || []).map((item, i) => (
                  <div key={i} style={{ marginBottom: 3 }}>
                    <span style={{ fontWeight: 'bold' }}>{item.qty || item.quantity || 1}x</span> {item.dish_name || item.name}
                    {item.notes && <div style={{ paddingLeft: 12, color: '#555', fontSize: 11 }}>↳ {item.notes}</div>}
                  </div>
                ))}
                <hr style={{ border: 'none', borderTop: '2px solid #000', margin: '6px 0' }} />
                <div style={{ fontWeight: 'bold' }}>
                  {fulfillmentMethod === 'delivery' ? '🚗 DELIVERY' : '🏃 PICKUP'}
                </div>
                {fulfillmentMethod === 'delivery' && deliveryAddress?.street && (
                  <div style={{ fontSize: 11 }}>
                    {deliveryAddress.street}<br />
                    {deliveryAddress.city}, {deliveryAddress.state}
                  </div>
                )}
                {pickupTime && <div style={{ fontWeight: 900, fontSize: 14, marginTop: 4 }}>TIME: {pickupTime}</div>}
              </>
            ) : (
              <>
                <div style={{ fontWeight: 900, fontSize: 14 }}>{reservationDate}</div>
                <div style={{ fontWeight: 900, fontSize: 14 }}>{reservationTime}</div>
                <div style={{ fontSize: 13 }}>Party of <strong>{partySize}</strong></div>
                {specialRequests && specialRequests !== 'none' && (
                  <div style={{ fontSize: 11, fontStyle: 'italic', marginTop: 4 }}>Notes: {specialRequests}</div>
                )}
              </>
            )}

            <hr style={{ border: 'none', borderTop: '2px solid #000', margin: '6px 0' }} />
            <div style={{ textAlign: 'center', fontSize: 10, color: '#aaa' }}>Printed via TableChat</div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: '1rem 1.4rem', borderTop: '1px solid var(--border)', display: 'flex', gap: '0.75rem' }} className="no-print">
          <button
            onClick={handlePrint}
            style={{ flex: 1, background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.75rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}
          >
            🖨️ Print Ticket
          </button>
          <button
            onClick={onClose}
            style={{ padding: '0.75rem 1rem', background: 'var(--warm)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
